<script setup lang="ts">
type TabsList = {
  deposit: string;
  withdraw: string;
  sports?: string;
  casino?: string;
  coupon: string;
  bonus: string;
  cashback?: string;
  clubevip?: string;
  all?: string;
};

type Props = {
  showOnlyThisTab?: keyof TabsList;
  onlyWalletTransactions?: boolean;
};

const { showOnlyThisTab = '', onlyWalletTransactions = false } =
  defineProps<Props>();

const { t } = useTexts();
const { gamificationActiveAndShow } = storeToRefs(useSmarticoConfigs());
const { getTransactions } = useWalletStore();
const { transactions } = storeToRefs(useWalletStore());
const { isRedeemableCashbackEnabled } = useFeatureFlagsJson();
const { openToaster } = useLayoutStore();

const isLoadingContent = ref<boolean>(false);
const activeTab = ref<string>(showOnlyThisTab || 'deposit');
const datePeriod = ref<number | string>('today');

const allTabsList = ref<TabsList>({
  deposit: t('account.wallet.deposits'),
  withdraw: t('account.wallet.withdrawals'),
  ...(!onlyWalletTransactions && {
    sports: t('account.wallet.bets'),
    casino: t('account.wallet.casino'),
  }),
  coupon: t('account.wallet.coupons'),
  bonus: t('account.wallet.bonus'),
  // full: t('account.wallet.all'),
  // all: t('account.wallet.all'),
});

if (isRedeemableCashbackEnabled.value) {
  allTabsList.value.cashback = t('account.wallet.cashback');
}

if (gamificationActiveAndShow.value) {
  allTabsList.value.clubevip = t('account.wallet.vip_club');
}

const tabsList = computed(() => {
  if (showOnlyThisTab) {
    return {
      [showOnlyThisTab]: allTabsList.value[showOnlyThisTab],
    };
  }

  return allTabsList.value;
});

const showTabsElement = computed(() => {
  return Object.keys(tabsList.value).length > 1;
});

const transactionData = computed(() => {
  const data = transactions.value;
  // Fix para error vindo do backend no of rows
  const rowsFix = data.total > data.last_page ? data.last_page : data.total;

  return {
    rows: rowsFix,
    currentPage: data.current_page,
    perPage: data.per_page,
    from: data.from,
    to: data.to,
    lastPage: data.last_page,
    data: data.data,
    total: data.total,
  };
});

async function getWalletTransactions(
  page: number,
  type: string,
  period: number | string,
) {
  isLoadingContent.value = true;

  await getTransactions(page, type, period)
    .then(() => {
      isLoadingContent.value = false;
    })
    .catch(() => {
      isLoadingContent.value = false;

      openToaster({
        message: t('error.general.something_wrong'),
        type: 'error',
      });
    });
}

function changeTab(index: number) {
  const type = Object.keys(tabsList.value)[index];

  activeTab.value = type || '';
  getWalletTransactions(1, type || '', datePeriod.value);
}

function goToTopOfTable() {
  const tableElement = document.querySelector(
    '.identifierToGoTop',
  ) as HTMLElement;

  const offsetTop = tableElement ? tableElement.offsetTop : 0;

  scrollToTop(offsetTop);
}

function changePage({ page }: { page: number }) {
  getWalletTransactions(page, activeTab.value, datePeriod.value);
  goToTopOfTable();
}

onBeforeMount(() => {
  getWalletTransactions(1, activeTab.value, datePeriod.value);
});

watch(datePeriod, () => {
  getWalletTransactions(1, activeTab.value, datePeriod.value);
});
</script>

<template>
  <UserBox>
    <div class="identifierToGoTop">
      <BaseLoading
        class="!bg-auth-bg-primary !rounded-md !absolute"
        :icon-loader="true"
        :show="isLoadingContent"
      />

      <div :class="$style.walletTransactionsTabs">
        <HeadlessTabGroup @change="changeTab">
          <div
            v-if="showTabsElement"
            :class="$style.walletFadeTab"
          >
            <HeadlessTabList :class="$style.walletTabsTabList">
              <HeadlessTab
                v-for="category in Object.values(tabsList)"
                :key="category"
                :ref="category"
                as="template"
              >
                <button :class="$style.tabButton">
                  {{ category }}
                </button>
              </HeadlessTab>
            </HeadlessTabList>
          </div>

          <div :class="$style.tablePeriods">
            <div :class="$style.innerContent">
              <UserWalletTablePeriods v-model="datePeriod" />
            </div>
          </div>

          <HeadlessTabPanels :class="[$style.walletTabPanel]">
            <HeadlessTabPanel
              v-for="(content, idx) in Object.values(tabsList)"
              :key="idx"
              class="rounded-xl ring-white outline-none"
            >
              <template v-if="transactionData.data.length">
                <UserWalletTable
                  :key="activeTab + 'old'"
                  :items="transactionData.data"
                  :table-type="activeTab"
                />
                <UserWalletPagination
                  :key="transactionData.currentPage"
                  :end="transactionData.to"
                  :last-page="transactionData.lastPage"
                  :page="transactionData.currentPage"
                  :per-page="transactionData.perPage"
                  :start="transactionData.from"
                  :total="transactionData.total"
                  @change-page="changePage"
                />
              </template>
              <div v-else-if="isLoadingContent">
                <UserWalletTableSkeleton />
              </div>
              <div
                v-else
                class="alert alert-warning mt-4"
              >
                {{ $t('account.wallet.table.empty_message') }}
              </div>
            </HeadlessTabPanel>
          </HeadlessTabPanels>
        </HeadlessTabGroup>
      </div>
    </div>
  </UserBox>
</template>

<style module lang="scss">
.walletTransactionsTabs {
  @apply w-full relative;

  .walletFadeTab {
    @apply min-w-full relative -mx-4 -mt-4 md:-mx-6 md:-mt-6 overflow-hidden  mb-4 md:mb-5;

    &:after {
      @apply content-[''] block w-[80px] h-full absolute -right-[3%] top-0 z-10 pointer-events-none bg-gradient-to-l from-bg-secondary;
    }
    &:before {
      @apply content-[''] w-full absolute bottom-0 left-0 right-0 border-b-2 border-b-texts/10 border-solid;
    }

    .walletTabsTabList {
      @apply w-full overflow-x-auto flex relative pr-10 pl-2 md:pl-4;

      scrollbar-width: none;
      -ms-overflow-style: none;

      .tabButton {
        @apply py-3 px-2 md:py-4 md:px-3 uppercase text-xs md:text-sm whitespace-nowrap border-b-2 border-b-texts/0 font-medium text-texts/60;

        &[aria-selected='true'] {
          @apply border-b-primary font-semibold text-texts;
        }
      }
    }
  }

  .tablePeriods {
    @apply relative overflow-hidden mb-2;

    &:after {
      @apply content-[''] block w-[80px] h-full absolute -right-[3%] top-0 z-10 pointer-events-none bg-gradient-to-l from-bg-secondary;
    }

    .innerContent {
      @apply w-full overflow-x-auto flex relative;

      scrollbar-width: none;
      -ms-overflow-style: none;
    }
  }
}

.walletTabPanel {
  @apply w-full mt-0;
}
</style>
